import { InputBaseComponentProps, Skeleton, SxProps, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ElementItemProps } from "../types";
import ElementFrame from "./ElementFrame";
import { useDevice } from "../../../../utils/DeviceProvider";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import { useIntl } from "react-intl";
import { hexInputCheck, patternInputCheck } from "../../basic/utils";
import { useLocale } from "../../../../locales/LocaleProvider";
import { getLabel } from "../../../../utils/utils";
import { ResetAdornment, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";

interface TxtboxParameters {
  hex?: boolean;
  maxLength?: number;
  number?: boolean;
  enabledCharacters?: string;
  inputProps?: InputBaseComponentProps;
  defaultValue?: string;
  notEmpty?: boolean;
}

const Txtbox = ({
  label: labelParam,
  configKey,
  elementKey,
  disabled,
  hex,
  maxLength,
  number,
  enabledCharacters,
  inputProps,
  defaultValue,
  notEmpty,
}: ElementItemProps<TxtboxParameters>) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [value, setValue] = useState<string | null>(null);

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const { openContextMenu } = useResetContextMenu();

  const label = getLabel(labelParam, locale);

  const onChange = useCallback(() => {
    if (configIndex === null || value === null) {
      return;
    }
    let val = hex && maxLength ? value.padStart(maxLength, "0") : value;
    if (notEmpty && defaultValue && !val) {
      val = defaultValue;
    }
    if (val !== value) {
      setValue(val);
    }
    currentValue[configKey].items[configIndex].value = val;
    setCurrentValue(currentValue);
  }, [setCurrentValue, currentValue, configKey, configIndex, value, hex, maxLength, defaultValue, notEmpty]);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex =
      currentValue[configKey]?.items?.findIndex(
        ({ type, key }: { type: string; key?: string }) => type === "TXTBOX" && (key === elementKey || `${key}` === elementKey)
      ) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, elementKey, setCurrentValue]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex]?.value ?? defaultValue ?? null;
    setSaved(svd);
    const current = currentValue[configKey].items[configIndex]?.value;
    setValue(current);
  }, [active, currentValue, config, configIndex, configKey, defaultValue]);

  if (value === null) {
    return (
      <ElementFrame label={label}>
        <Skeleton variant="rectangular" height={40} sx={{ width: "100%", borderRadius: 1 }} />
      </ElementFrame>
    );
  }

  if (configIndex === -1) {
    return (
      <ElementFrame label={label}>
        <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>
      </ElementFrame>
    );
  }

  return (
    <TxtboxElement
      label={label}
      value={value ?? ""}
      onChange={setValue}
      onBlur={onChange}
      edited={saved !== value}
      disabled={disabled}
      hex={hex}
      maxLength={maxLength}
      number={number}
      enabledCharacters={enabledCharacters}
      inputProps={inputProps}
      onContextMenu={saved !== value ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value`) : undefined}
    />
  );
};

export const TxtboxElement = ({
  label,
  value,
  onChange,
  sx,
  inputSx,
  disabled,
  edited,
  maxLength,
  hex,
  number,
  enabledCharacters,
  inputProps,
  onBlur,
  onContextMenu,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  sx?: SxProps;
  inputSx?: SxProps;
  disabled?: boolean;
  edited?: boolean;
  maxLength?: number;
  hex?: boolean;
  number?: boolean;
  enabledCharacters?: string;
  inputProps?: InputBaseComponentProps;
  onBlur?: () => void;
  onContextMenu?: React.MouseEventHandler<HTMLElement>;
}) => {
  const editedStyle = useEditedStyles();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (hex) {
        return hexInputCheck(e.target.value, onChange);
      } else if (enabledCharacters) {
        return patternInputCheck(e.target.value, enabledCharacters, onChange);
      } else {
        return onChange(e.target.value);
      }
    },
    [onChange, hex, enabledCharacters]
  );

  return (
    <ElementFrame label={label} sx={sx} disabled={disabled}>
      <TextField
        hiddenLabel
        variant="filled"
        value={value}
        inputProps={{ maxLength, style: hex ? { textTransform: "uppercase" } : {}, type: number ? "number" : undefined, ...inputProps }}
        className={edited ? editedStyle.input : ""}
        onChange={handleChange}
        onBlur={onBlur}
        sx={{ margin: 0, minWidth: 10, ...inputSx }}
        size="small"
        disabled={disabled}
        fullWidth
        autoComplete="off"
        InputProps={onContextMenu ? { endAdornment: <ResetAdornment /> } : undefined}
        onContextMenu={onContextMenu}
      />
    </ElementFrame>
  );
};

export default Txtbox;
